import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useCallback, useState } from 'react';
import { ButtonWithIcon } from 'src/components/Buttons';
import { ReloadButton } from 'src/components/Buttons/ReloadButton';
import { Content } from 'src/contexts/ContentContext';
import { vars } from 'src/core/themes';
import { Button, DropdownMenu } from 'src/core/ui';
import { RotatingWrapper } from 'src/core/ui/AnimatingWrapper';
import { useGenerateGroupInvoicePdf } from 'src/hooks/api/useGenerateGroupInvoicePdf';
import { SALES_GROUP_QUERY_KEY } from 'src/hooks/api/useGetSalesGroup';
import { useRefundToCredit } from 'src/hooks/api/useRefundToCredit';
import { ModalFooter } from 'src/modals/Modal/Modal.styled';
import * as saleDetailsStyles from 'src/modals/SaleDetails/SaleDetails.css';
import { DownloadIcon, ProcessingIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import {
  MarketplacePaymentState,
  Sale,
  SalesGroupDetails,
} from 'src/WebApiController';

/**
 * Sales Group modal footer (POS-8632).
 * Provides Refund to Credit bulk action over the group's eligible member sales.
 * Mirrors SaleDetailsFooter's layout: ModalFooter wrapping the right-aligned
 * styles.actionsContainer row.
 */
export function SalesGroupModalFooter({
  salesGroupId,
  sales,
  group,
}: {
  salesGroupId: string;
  sales: Sale[];
  group: SalesGroupDetails;
}) {
  const queryClient = useQueryClient();
  const refundToCredit = useRefundToCredit();
  const [isRefunding, setIsRefunding] = useState(false);

  // POS-8856: shared group-invoice generation. React Query dedupes the underlying
  // getSalesGroup / getCatalogSlim fetches with the modal's existing queries.
  const { generateBlob, isPreparing } =
    useGenerateGroupInvoicePdf(salesGroupId);
  const [isDownloading, setIsDownloading] = useState(false);

  // POS-8856: build the GROUP invoice PDF and DOWNLOAD it as a file (mirrors
  // generateAndDownloadGroupPDF in LaunchDownloadRetailSaleInvoice). Null blob → no-op.
  const onPrintInvoice = useCallback(async () => {
    setIsDownloading(true);
    try {
      const blob = await generateBlob();
      if (!blob) {
        return;
      }
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `RetailGroupInvoice_${salesGroupId}_${format(
        new Date(),
        'yyyy-MM-dd'
      )}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } finally {
      setIsDownloading(false);
    }
  }, [generateBlob, salesGroupId]);

  const printInvoiceDisabled =
    isPreparing || isDownloading || sales.length === 0;
  const showPrintProcessing = isPreparing || isDownloading;

  // POS-8632: refetch the group payload so the modal reflects the latest state
  // after out-of-band changes (payments, per-sale edits made elsewhere, etc.).
  const refreshGroup = () =>
    queryClient.invalidateQueries({
      queryKey: [SALES_GROUP_QUERY_KEY, salesGroupId],
    });

  // POS-8726: Group-level refund button. Visible when the group has Paid payment
  // lines (the group is paid), hidden when all lines are already reversed/pending.
  const paymentLines = group.aggregatedPaymentLines ?? [];
  const hasPaidPaymentLines = paymentLines.some(
    (l) => l.state === MarketplacePaymentState.Paid
  );
  const allLinesRefunded =
    paymentLines.length > 0 &&
    paymentLines.every(
      (l) =>
        l.state === MarketplacePaymentState.Reversed ||
        l.state === MarketplacePaymentState.PendingReversal
    );
  const showRefund = hasPaidPaymentLines && !allLinesRefunded;

  // POS-8726: Refund each member sale in the group to buyer credit.
  // Send amount=0 to let the backend compute the correct per-sale refund amount
  // from the actual (non-aggregated) payment lines. The frontend's aggregatedPaymentLines
  // merge lines across sales, making per-sale amount computation unreliable.
  const handleRefundGroup = useCallback(async () => {
    const memberSales = group.sales ?? [];
    if (memberSales.length === 0) return;

    setIsRefunding(true);
    try {
      for (const sale of memberSales) {
        await refundToCredit.mutateAsync({
          saleId: sale.id,
          amount: 0, // backend computes from sale's Paid lines
          notes: null,
        });
      }
      await queryClient.invalidateQueries({
        queryKey: [SALES_GROUP_QUERY_KEY, salesGroupId],
      });
    } finally {
      setIsRefunding(false);
    }
  }, [
    group.sales,
    group.aggregatedPaymentLines,
    refundToCredit,
    queryClient,
    salesGroupId,
  ]);

  return (
    <ModalFooter>
      <div className={saleDetailsStyles.actionsContainer}>
        {/* POS-8726: Group-level Refund dropdown — left side of footer */}
        {showRefund && (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild disabled={isRefunding}>
              <Button variant="outline" disabled={isRefunding}>
                <Content id={ContentId.Refund} />
              </Button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content align="start">
                <DropdownMenu.Item onSelect={handleRefundGroup}>
                  <Content id={ContentId.RefundToCredit} />
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        )}
        {/*
          POS-8856: Download the full multi-event GROUP invoice PDF as a file.
          Mirrors LaunchDownloadRetailSaleInvoice's ButtonWithIcon + RotatingWrapper
          processing pattern; outline variant so it matches the footer's other buttons.
        */}
        <ButtonWithIcon
          variant="outline"
          disabled={printInvoiceDisabled}
          textContentId={ContentId.PrintInvoice}
          titleContentId={ContentId.PrintInvoice}
          onClick={printInvoiceDisabled ? undefined : onPrintInvoice}
          icon={
            showPrintProcessing ? (
              <RotatingWrapper>
                <ProcessingIcon fill={vars.color.textBrand} />
              </RotatingWrapper>
            ) : (
              <DownloadIcon
                withHoverEffect
                disabled={printInvoiceDisabled}
                fill={vars.color.textBrand}
                size={vars.iconSize.m}
              />
            )
          }
        />
        <ReloadButton
          variant="outline"
          textContentId={ContentId.Refresh}
          titleContentId={ContentId.Refresh}
          onClick={refreshGroup}
        />
      </div>
    </ModalFooter>
  );
}
